import { useEffect, useMemo, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { UserPlus, Eye, Send, Plus, Trash2, ShieldCheck } from "lucide-react";
import { ISO_COUNTRY_OPTIONS } from "@/data/isoCountries";
import { CapabilityNotice } from "@/components/bse/BseUatForm";
import { useAdminBseCapability } from "@/components/BseCapabilityStatus";

// Variant catalogue — each entry knows how to assemble its `spec` from the flat form state.
const VARIANTS = [
  { key: "individual", label: "Individual", tax: "01", blurb: "Resident, single holder, physical" },
  { key: "demat", label: "Demat", tax: "01", blurb: "Resident individual, demat mode" },
  { key: "both", label: "Physical + Demat", tax: "01", blurb: "Resident individual with both holding modes" },
  { key: "minor", label: "Minor", tax: "02", blurb: "On behalf of minor + guardian" },
  { key: "nri", label: "NRI", tax: "21/24", blurb: "NRE / NRO with foreign address" },
  { key: "joint", label: "Joint", tax: "01", blurb: "2–3 holders, JO / AS" },
  { key: "non_individual", label: "Non-Individual", tax: "05/55", blurb: "AOP / FPI / QFI with UBO" },
];

const GENDER_OPTIONS = [
  { value: "M", label: "M — Male" },
  { value: "F", label: "F — Female" },
  { value: "O", label: "O — Other" },
];
const OCCUPATION_OPTIONS = [
  { value: "01", label: "01 — Business" },
  { value: "02", label: "02 — Service" },
  { value: "03", label: "03 — Professional" },
  { value: "04", label: "04 — Agriculturist" },
  { value: "05", label: "05 — Retired" },
  { value: "06", label: "06 — Housewife" },
  { value: "07", label: "07 — Student" },
  { value: "08", label: "08 — Others" },
  { value: "09", label: "09 — Doctor" },
  { value: "41", label: "41 — Private-sector service" },
  { value: "42", label: "42 — Public-sector service" },
  { value: "43", label: "43 — Forex dealer" },
  { value: "44", label: "44 — Government service" },
  { value: "99", label: "99 — Unknown / not applicable" },
];
const BANK_ACCOUNT_OPTIONS = [
  { value: "SB", label: "SB — Savings account" },
  { value: "CB", label: "CB — Current account" },
  { value: "NE", label: "NE — NRE account" },
  { value: "NO", label: "NO — NRO account" },
];
const DEPOSITORY_OPTIONS = [
  { value: "CDSL", label: "CDSL — Central Depository Services" },
  { value: "NSDL", label: "NSDL — National Securities Depository" },
];
const HOLDING_OPTIONS = [
  { value: "JO", label: "JO — Joint" },
  { value: "AS", label: "AS — Anyone or Survivor" },
];
const GUARDIAN_RELATION_OPTIONS = [
  { value: "5", label: "5 — Father" },
  { value: "12", label: "12 — Mother" },
  { value: "23", label: "23 — Court-appointed legal guardian" },
];
const NON_INDIVIDUAL_TAX_OPTIONS = [
  ["03", "HUF"], ["04", "Company"], ["05", "AOP"], ["06", "Partnership firm"],
  ["07", "Body corporate"], ["08", "Trust"], ["09", "Society"], ["10", "Others"],
  ["11", "NRI-Others"], ["12", "DFI"], ["13", "Sole proprietorship"], ["22", "OCB"],
  ["23", "FII"], ["25", "Overseas corporate body — others"], ["27", "NRI-HUF (NRO)"],
  ["29", "NRI-HUF (NRE)"], ["31", "Provident fund"], ["32", "Superannuation fund"],
  ["33", "Gratuity fund"], ["34", "Pension fund"], ["36", "Mutual-fund FOF scheme"],
  ["37", "NPS trust"], ["38", "Global Development Network"], ["39", "FCRA"],
  ["43", "QFI — Corporate"], ["44", "QFI — Pension fund"], ["45", "QFI — Hedge fund"],
  ["46", "QFI — Mutual fund"], ["47", "LLP"], ["48", "Non-profit organisation"],
  ["51", "Public limited company"], ["52", "Private limited company"],
  ["53", "Unlisted company"], ["54", "Mutual fund"], ["55", "FPI — Category I"],
  ["56", "FPI — Category II"], ["57", "FPI — Category III"],
  ["58", "Financial institution"], ["59", "Body of individuals"],
  ["60", "Insurance company"], ["72", "Government body"], ["73", "Defence establishment"],
  ["74", "Non-government organisation"], ["75", "Bank / co-operative bank"],
  ["76", "Artificial juridical person"],
].map(([value, meaning]) => ({ value, label: `${value} — ${meaning}` }));
const CORPORATE_SECTOR_OPTIONS = [
  { value: "1", label: "1 — Foreign exchange / money changer" },
  { value: "2", label: "2 — Gaming / gambling / lottery" },
  { value: "3", label: "3 — Money lending / pawning" },
  { value: "4", label: "4 — Not applicable" },
];
const EXEMPTION_OPTIONS = [
  ["A", "Tax-exempt organisation / retirement plan"], ["B", "United States agency"],
  ["C", "US state or political subdivision"], ["D", "Publicly traded corporation"],
  ["E", "Affiliate of publicly traded corporation"], ["F", "Registered securities dealer"],
  ["G", "Real-estate investment trust"], ["H", "Regulated investment company"],
  ["I", "Common trust fund"], ["J", "Bank"], ["K", "Broker"],
  ["L", "Tax-exempt trust"], ["M", "403(b) / 457(g) tax-exempt trust"], ["N", "Not applicable"],
].map(([value, meaning]) => ({ value, label: `${value} — ${meaning}` }));
const FFI_OPTIONS = [
  { value: "", label: "Not applicable" },
  { value: "FFI", label: "FFI — Foreign Financial Institution" },
  { value: "DRNFE", label: "DRNFE — Direct Reporting NFE" },
];
const GIIN_OPTIONS = [
  { value: "AF", label: "AF — Applied for" },
  { value: "NR", label: "NR — Not required to apply" },
  { value: "NO", label: "NO — Not obtained / non-participating FI" },
];
const INCOME_SLAB_OPTIONS = [
  ["31", "Below ₹1 lakh"], ["32", "Above ₹1 lakh up to ₹5 lakh"],
  ["33", "Above ₹5 lakh up to ₹10 lakh"], ["34", "Above ₹10 lakh up to ₹25 lakh"],
  ["35", "Above ₹25 lakh up to ₹1 crore"], ["36", "Above ₹1 crore"],
].map(([value, meaning]) => ({ value, label: `${value} — ${meaning}` }));
const UBO_TYPE_OPTIONS = [
  ["C01", "Legal person — ownership"], ["C02", "Legal person — other means"],
  ["C03", "Legal person — senior managing official"], ["C04", "Trust — settlor"],
  ["C05", "Trust — trustee"], ["C06", "Trust — protector"], ["C07", "Trust — beneficiary"],
  ["C08", "Trust — other"], ["C09", "Equivalent arrangement — settlor"],
  ["C10", "Equivalent arrangement — trustee"], ["C11", "Equivalent arrangement — protector"],
  ["C12", "Equivalent arrangement — beneficiary"], ["C13", "Equivalent arrangement — other"],
  ["C14", "Unknown"],
].map(([value, meaning]) => ({ value, label: `${value} — ${meaning}` }));
const UBO_CATEGORY_OPTIONS = [
  { value: "UBO", label: "UBO — Ultimate beneficial owner" },
  { value: "SMO", label: "SMO — Senior managing official" },
];
const NOMINEE_RELATION_OPTIONS = [
  ["1", "Aunt"], ["2", "Brother"], ["3", "Daughter"], ["4", "Daughter-in-law"],
  ["5", "Father"], ["6", "Father-in-law"], ["7", "Granddaughter"], ["8", "Grandson"],
  ["9", "Grandfather"], ["10", "Grandmother"], ["11", "Husband"], ["12", "Mother"],
  ["13", "Mother-in-law"], ["14", "Nephew"], ["15", "Niece"], ["16", "Friend"],
  ["17", "Sister"], ["18", "Son"], ["19", "Son-in-law"], ["20", "Uncle"],
  ["21", "Wife"], ["22", "Others"], ["24", "Brother-in-law"],
  ["25", "Sister-in-law"], ["26", "Spouse"],
].map(([value, meaning]) => ({ value, label: `${value} — ${meaning}` }));
const ACCOUNT_OWNER_OPTIONS = [
  { value: "SELF", label: "SELF — Investor" },
  { value: "GUARDIAN", label: "GUARDIAN — Guardian" },
];
const PEP_OPTIONS = [
  { value: "N", label: "N — Not politically exposed" },
  { value: "Y", label: "Y — Politically exposed person" },
  { value: "R", label: "R — Relative of a politically exposed person" },
];
const UCC_HOLDING_OPTIONS = [
  { value: "SI", label: "SI — Single" }, { value: "JO", label: "JO — Joint" },
  { value: "AS", label: "AS — Anyone or Survivor" },
];
const IDENTIFIER_OPTIONS = [
  { value: "pan", label: "PAN" }, { value: "aadhaar", label: "Aadhaar" },
  { value: "passport", label: "Passport" }, { value: "driving_license", label: "Driving licence" },
  { value: "cancel_cheque", label: "Cancelled cheque" }, { value: "bank_statement", label: "Bank statement" },
  { value: "cml_copy", label: "Client master list (CML)" }, { value: "aof", label: "Account opening form" },
  { value: "nomination_form", label: "Nomination form" }, { value: "fatca_form", label: "FATCA form" },
  { value: "address_proof", label: "Address proof" }, { value: "supporting_document", label: "Supporting document" },
];

const F = (v) => (v == null ? "" : v);
const BULK_HEADERS = ["client_code", "first_name", "last_name", "dob", "gender", "pan", "email", "phone",
  "occupation_code", "address_line_1", "city", "state", "pincode", "ifsc", "account_number", "account_type"];

function parseCsv(text) {
  const records = [];
  let row = [], value = "", quoted = false;
  const pushValue = () => { row.push(value); value = ""; };
  const pushRow = () => { if (row.some((cell) => cell.trim())) records.push(row); row = []; };
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"') {
      if (quoted && text[index + 1] === '"') { value += '"'; index += 1; } else quoted = !quoted;
    } else if (char === "," && !quoted) pushValue();
    else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      pushValue(); pushRow();
    } else value += char;
  }
  pushValue(); pushRow();
  if (!records.length) return [];
  const headers = records[0].map((header) => header.trim().toLowerCase());
  const missing = BULK_HEADERS.filter((header) => !headers.includes(header));
  if (missing.length) throw new Error(`CSV is missing columns: ${missing.join(", ")}`);
  return records.slice(1).map((cells) => Object.fromEntries(headers.map((header, index) => [header, (cells[index] || "").trim()])))
    .filter((item) => item.client_code);
}

// --- small field helpers -------------------------------------------------
function Field({ label, hint, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-[var(--ink-muted)]">{label}</Label>
      <Input {...props} className="h-9" />
      {hint && <p className="text-[10px] text-[var(--ink-muted)]">{hint}</p>}
    </div>
  );
}

function SelectField({ label, hint, options, placeholder, ...props }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-[var(--ink-muted)]">{label}</Label>
      <select {...props} className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm">
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((option) => <option key={option.value || "__blank"} value={option.value}>{option.label}</option>)}
      </select>
      {hint && <p className="text-[10px] text-[var(--ink-muted)]">{hint}</p>}
    </div>
  );
}

function Group({ title, children, right }) {
  return (
    <div className="rounded-xl border border-[var(--glass-border)] bg-[var(--surface-2)]/40 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--ink-muted)]">{title}</h3>
        {right}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}

const emptyHolder = () => ({ first_name: "", last_name: "", dob: "", gender: "M", pan: "", occ_code: "01" });
const emptyUbo = () => ({ first_name: "", last_name: "", dob: "", pan: "", beneficiary_percent: "", ubo_type_code: "C01", ubo_category: "UBO" });

// --- spec builders per variant (mirror bse_v2.ucc_builder signatures) -----
function buildSpec(variant, s) {
  const contact = { email: s.email, phone: s.phone };
  const bank = { ifsc: s.bank_ifsc, acc_num: s.bank_acc, acc_type: s.bank_type || undefined };
  const comm = { line1: s.addr1, city: s.city, state: s.state, pincode: s.pincode };
  const foreign = { line1: s.f_line1, city: s.f_city, state: s.f_state, country: s.f_country, pincode: s.f_pincode };
  if (variant === "individual" || variant === "demat" || variant === "both") {
    const kyc = {
      pan: s.pan, dob: s.dob, first_name: s.first_name, last_name: s.last_name, gender: s.gender,
      city: s.city, state: s.state, pincode: s.pincode, bank_ifsc: s.bank_ifsc,
      bank_account_no: s.bank_acc, account_type: s.bank_type || "SB", occupation_code: s.occ_code || "01",
    };
    if (variant === "demat" || variant === "both") Object.assign(kyc, { depository_code: s.dp_code || "CDSL", dp_id: s.dp_id, dp_client_id: s.dp_client_id });
    return { ...contact, full_name: `${s.first_name} ${s.last_name}`.trim(), kyc };
  }
  if (variant === "minor") {
    return {
      ...contact, bank, comm,
      minor: { first_name: s.first_name, last_name: s.last_name, dob: s.dob, gender: s.gender, pan: s.pan || "", occ_code: s.occ_code || "07" },
      guardian: { first_name: s.g_first, last_name: s.g_last, dob: s.g_dob, gender: s.g_gender || "M",
        pan: s.g_pan || "", occ_code: s.g_occ_code || "02", pan_exempt_ref_no: s.g_pan ? "" : s.g_exempt_ref, guardian_relation: s.g_relation || "5" },
    };
  }
  if (variant === "nri") {
    return { ...contact, kind: s.nri_kind || "NRE", bank, comm, foreign,
      holder: { first_name: s.first_name, last_name: s.last_name, dob: s.dob, gender: s.gender, pan: s.pan, occ_code: s.occ_code || "01" } };
  }
  if (variant === "joint") {
    return { ...contact, bank, comm, holding_nature: s.holding_nature || "JO", holders: s.holders || [] };
  }
  if (variant === "non_individual") {
    return {
      ...contact, tax_code: s.tax_code || "05", entity_name: s.entity_name, pan: s.pan, bank,
      comm: s.f_country ? undefined : comm, foreign: s.f_country ? foreign : undefined,
      incorporation_date: s.incorporation_date, occ_code: s.occ_code || "03",
      corporate_service_sector: s.corporate_service_sector || "4", exemption_code: s.exemption_code || "N",
      ffi_drnfe: s.ffi_drnfe || "", is_giin_avail: s.is_giin_avail || "NR",
      income_slab: s.income_slab || "31",
      ubo_details: (s.ubos || []).map((u) => ({
        first_name: u.first_name, last_name: u.last_name, dob: u.dob, pan: u.pan,
        beneficiary_percent: Number(u.beneficiary_percent) || 0,
        ubo_type_code: u.ubo_type_code || "C01", ubo_category: u.ubo_category || "UBO",
      })),
    };
  }
  return {};
}

export default function UccOnboarding() {
  const capability = useAdminBseCapability("ucc_registration");
  const [variant, setVariant] = useState("individual");
  const [s, setS] = useState({
    gender: "M", occ_code: "01", bank_type: "SB", holding_nature: "JO", nri_kind: "NRE",
    g_gender: "M", g_occ_code: "02", g_relation: "5", tax_code: "05",
    corporate_service_sector: "4", exemption_code: "N", ffi_drnfe: "",
    is_giin_avail: "NR", income_slab: "31", dp_code: "CDSL",
  });
  const [holders, setHolders] = useState([emptyHolder(), emptyHolder()]);
  const [ubos, setUbos] = useState([emptyUbo()]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [health, setHealth] = useState(null);
  const [ops, setOps] = useState([]);
  const [maintBusy, setMaintBusy] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [maint, setMaint] = useState({ client_code: "", field: "contact", mutation: "add", holder_rank: "1",
    email: "", phone: "", ifsc: "", acc_num: "", acc_type: "SB", account_owner: "SELF", dp_code: "CDSL",
    dp_id: "", dp_client_id: "", nominee_name: "", nominee_dob: "", nominee_pan: "", nominee_gender: "M",
    nominee_relation: "10", first_name: "", middle_name: "", last_name: "", dob: "", gender: "M", pan: "",
    occ_code: "01", income_slab: "31", pep: "N", country_of_birth: "IND", city_of_birth: "",
    addr1: "", addr2: "", addr3: "", city: "", state: "", country: "IND", pincode: "",
    holding_nature: "JO", identifier_type: "pan", identifier_number: "", file_name: "", file_base64: "", file_size: 0 });

  const set = (k) => (e) => setS((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    adminApi.get("/admin/bse2/health").then((r) => setHealth(r.data)).catch(() => {});
    adminApi.get("/admin/bse2/ucc/ops").then((r) => setOps(r.data.ops || [])).catch(() => {});
  }, []);

  const refreshOps = () => adminApi.get("/admin/bse2/ucc/ops").then((r) => setOps(r.data.ops || []));
  const setMaintField = (key) => (event) => setMaint((prev) => ({ ...prev, [key]: event.target.value }));
  const maintenanceSpec = () => {
    if (maint.field === "contact") return { holder_rank: maint.holder_rank, occ_code: maint.occ_code,
      contact: { email: maint.email, phone: maint.phone, contact_type: "PR" } };
    if (maint.field === "bank") return { [maint.mutation]: [{ ifsc: maint.ifsc.toUpperCase(), acc_num: maint.acc_num, acc_type: maint.acc_type, account_owner: maint.account_owner }] };
    if (maint.field === "depository") return { [maint.mutation]: [{ code: maint.dp_code, dp_id: maint.dp_id, client_id: maint.dp_client_id, bank_account: maint.acc_num, account_owner: maint.account_owner }] };
    if (maint.field === "nominee") return { holder_rank: maint.holder_rank, nominees: [{ name: maint.nominee_name, dob: maint.nominee_dob, gender: maint.nominee_gender, pan: maint.nominee_pan.toUpperCase(), percent: 100, relation: maint.nominee_relation }], nominee_soa: true };
    if (maint.field === "comm_addr" || maint.field === "foreign_addr") return { address: { line1: maint.addr1, line2: maint.addr2, line3: maint.addr3, city: maint.city, state: maint.state, country: maint.country, pincode: maint.pincode } };
    if (maint.field === "fatca") return { holder_rank: maint.holder_rank, pan: maint.pan.toUpperCase(),
      person: { first_name: maint.first_name, middle_name: maint.middle_name, last_name: maint.last_name, dob: maint.dob, gender: maint.gender },
      fatca_extra: { place_of_birth: maint.city_of_birth, country_of_birth: maint.country_of_birth,
        occ_code: maint.occ_code, income_slab: maint.income_slab, politically_exposed: maint.pep } };
    if (maint.field === "holder") return { holder_rank: maint.holder_rank, pan: maint.pan.toUpperCase(), occ_code: maint.occ_code,
      person: { first_name: maint.first_name, middle_name: maint.middle_name, last_name: maint.last_name, dob: maint.dob, gender: maint.gender },
      contact: { email: maint.email, phone: maint.phone }, include_fatca: true,
      fatca_extra: { city: maint.city_of_birth, country_of_birth: maint.country_of_birth,
        occ_code: maint.occ_code, income_slab: maint.income_slab, pep: maint.pep } };
    if (maint.field === "identifier") return { operation: maint.mutation, identifier: {
      identifier_type: maint.identifier_type, identifier_number: maint.identifier_number,
      file_name: maint.file_name, file_blob: maint.file_base64, file_size: maint.file_size } };
    if (maint.field === "holding_nature") return { holding_nature: maint.holding_nature,
      holders: maint.holding_nature === "SI" ? [] : [{ rank: "2", pan: maint.pan.toUpperCase(), occ_code: maint.occ_code,
        person: { first_name: maint.first_name, middle_name: maint.middle_name, last_name: maint.last_name, dob: maint.dob, gender: maint.gender },
        contact: { email: maint.email, phone: maint.phone } }] };
    return {};
  };
  const maintenanceFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setMaint((previous) => ({ ...previous, file_name: file.name, file_size: file.size,
      file_base64: String(reader.result).split(",").pop() }));
    reader.readAsDataURL(file);
  };
  const readBulkCsv = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try { const rows = parseCsv(String(reader.result)); setBulkRows(rows); setBulkResult(null); toast.success(`${rows.length} UCC row(s) loaded`); }
      catch (error) { setBulkRows([]); toast.error(error.message); }
    };
    reader.readAsText(file);
  };
  const downloadBulkTemplate = () => {
    const sample = [BULK_HEADERS.join(","), "SMIFSUAT001,Asha,Tester,1990-01-01,F,ABCDE1234F,asha@example.com,9876543210,02,10 Test Road,Kolkata,West Bengal,700001,HDFC0000060,1234567890,SB"].join("\n");
    const url = URL.createObjectURL(new Blob([sample], { type: "text/csv" }));
    const link = document.createElement("a"); link.href = url; link.download = "bse_ucc_bulk_template.csv"; link.click(); URL.revokeObjectURL(url);
  };
  const submitBulkCsv = async () => {
    if (!bulkRows.length) return toast.error("Choose a populated BSE UCC CSV first");
    if (!window.confirm(`Send ${bulkRows.length} individual UCC row(s) through BSE's CSV runner in UAT?`)) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc-csv/individuals", { rows: bulkRows });
      setBulkResult(data); await refreshOps();
      data.status === "success" ? toast.success(`${data.count} UCC row(s) processed by BSE`) : toast.error("Some CSV rows were rejected; inspect the results");
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setBulkBusy(false); }
  };
  const updateUcc = async (dryRun) => {
    if (!maint.client_code) return toast.error("Enter the BSE client code to update");
    setMaintBusy(true);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/update", { client_code: maint.client_code.toUpperCase(), field: maint.field, spec: maintenanceSpec(), dry_run: dryRun });
      if (dryRun) { setPreview(data.payload); toast.success("Update payload built for review"); }
      else if (data.status === "SUBMITTED") { toast.success(data.message || "UCC update submitted to BSE"); await refreshOps(); }
      else toast.error(data.message || `BSE did not accept the UCC update (${data.status || "unknown status"})`);
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setMaintBusy(false); }
  };
  const deactivateUcc = async () => {
    if (!maint.client_code) return toast.error("Enter the BSE client code to deactivate");
    if (!window.confirm(`Deactivate UCC ${maint.client_code.toUpperCase()} at BSE? This blocks new transactions.`)) return;
    setMaintBusy(true);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/deactivate", { client_code: maint.client_code.toUpperCase() });
      data.status === "SUBMITTED" ? toast.success(data.message) : toast.error(data.message || `BSE returned ${data.status}`);
      await refreshOps();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setMaintBusy(false); }
  };

  const clientCode = s.client_code || "";
  const fullSpec = useMemo(() => {
    const merged = { ...s, holders: holders, ubos: ubos };
    return buildSpec(variant, merged);
  }, [variant, s, holders, ubos]);

  const call = async (dry) => {
    if (!clientCode) { toast.error("Client code is required"); return; }
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/bse2/ucc/create-variant", {
        client_code: clientCode, variant, spec: fullSpec, dry_run: dry,
      });
      if (dry) { setPreview(data.payload); toast.success("Payload built — review the wire preview"); }
      else {
        setPreview(null);
        data.status === "SUBMITTED"
          ? toast.success(`UCC ${clientCode} submitted to BSE (${variant})`)
          : toast.error(data.message || "BSE rejected the registration");
      }
    } catch (err) { toast.error(getApiErrorMessage(err)); }
    finally { setBusy(false); }
  };

  const cur = VARIANTS.find((v) => v.key === variant);
  const chooseVariant = (key) => {
    setVariant(key);
    setPreview(null);
    setS((previous) => ({
      ...previous,
      occ_code: key === "minor" ? "07" : key === "non_individual" ? "03" : "01",
      bank_type: key === "nri"
        ? (previous.nri_kind === "NRO" ? "NO" : "NE")
        : key === "non_individual" ? "CB" : "SB",
    }));
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]" data-testid="ucc-onboarding-title">UCC Onboarding</h1>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">
            Register any investor profile on BSE StAR MF. Build the exact wire payload, preview it, then submit.
          </p>
        </div>
        {health && (
          <HealthPill health={health} />
        )}
      </div>
      <div className="mt-4">
        <CapabilityNotice capability={capability} title="UCC registration and maintenance" />
      </div>

      {/* Variant picker — a real taxonomy, not decorative steps */}
      <div className="mt-6 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {VARIANTS.map((v) => {
          const active = v.key === variant;
          return (
            <button
              key={v.key}
              onClick={() => chooseVariant(v.key)}
              data-testid={`variant-${v.key}`}
              className={`rounded-xl border p-3 text-left transition ${
                active
                  ? "border-[var(--cta)] bg-[var(--cta)]/10 shadow-sm"
                  : "border-[var(--glass-border)] hover:border-[var(--cta)]/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">{v.label}</span>
                <span className="text-[10px] text-[var(--ink-muted)]">tax {v.tax}</span>
              </div>
              <p className="mt-1 text-[11px] leading-tight text-[var(--ink-muted)]">{v.blurb}</p>
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.35fr_1fr]">
        {/* form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <UserPlus className="h-4 w-4 text-[var(--cta)]" /> {cur.label} registration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Group title="Identity">
              <Field label="Client code (UCC)" value={F(s.client_code)} onChange={set("client_code")} placeholder="e.g. SMIFS100234" />
              {variant === "joint" ? (
                <div className="flex items-center rounded-lg border border-dashed border-[var(--glass-border)] px-3 text-xs text-[var(--ink-muted)]">
                  Add the primary and joint-holder identities in the Holders section below.
                </div>
              ) : variant === "non_individual" ? <>
                <Field label="Entity name" value={F(s.entity_name)} onChange={set("entity_name")} />
                <Field label="PAN" value={F(s.pan)} onChange={set("pan")} placeholder="ABCPE1234F" />
                <Field label="Incorporation date" type="date" value={F(s.incorporation_date)} onChange={set("incorporation_date")} />
                <SelectField label="Tax status" value={F(s.tax_code)} onChange={set("tax_code")} options={NON_INDIVIDUAL_TAX_OPTIONS} />
                <SelectField label="Occupation" value={F(s.occ_code)} onChange={set("occ_code")} options={OCCUPATION_OPTIONS} />
              </> : <>
                <Field label="First name" value={F(s.first_name)} onChange={set("first_name")} />
                <Field label="Last name" value={F(s.last_name)} onChange={set("last_name")} />
                <Field label="PAN" value={F(s.pan)} onChange={set("pan")} placeholder="ABCPE1234F" />
                <Field label="Date of birth" type="date" value={F(s.dob)} onChange={set("dob")} />
                <SelectField label="Gender" value={F(s.gender)} onChange={set("gender")} options={GENDER_OPTIONS} />
                <SelectField label="Occupation" value={F(s.occ_code)} onChange={set("occ_code")} options={OCCUPATION_OPTIONS} />
              </>}
            </Group>

            <Group title="Contact">
              <Field label="Email" type="email" value={F(s.email)} onChange={set("email")} />
              <Field label="Mobile" value={F(s.phone)} onChange={set("phone")} placeholder="9830000000" />
            </Group>

            {variant === "nri" && (
              <Group title="NRI type">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-[var(--ink-muted)]">Account type</Label>
                  <div className="flex gap-2">
                    {["NRE", "NRO"].map((k) => (
                      <button key={k} onClick={() => setS((p) => ({ ...p, nri_kind: k, bank_type: k === "NRE" ? "NE" : "NO" }))}
                        className={`flex-1 rounded-lg border px-3 py-2 text-sm ${s.nri_kind === k ? "border-[var(--cta)] bg-[var(--cta)]/10 font-semibold" : "border-[var(--glass-border)]"}`}>
                        {k} <span className="text-[10px] text-[var(--ink-muted)]">tax {k === "NRE" ? "21" : "24"}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </Group>
            )}

            {variant === "minor" && (
              <Group title="Guardian">
                <Field label="Guardian first name" value={F(s.g_first)} onChange={set("g_first")} />
                <Field label="Guardian last name" value={F(s.g_last)} onChange={set("g_last")} />
                <Field label="Guardian DOB" type="date" value={F(s.g_dob)} onChange={set("g_dob")} />
                <SelectField label="Guardian gender" value={F(s.g_gender)} onChange={set("g_gender")} options={GENDER_OPTIONS} />
                <SelectField label="Guardian occupation" value={F(s.g_occ_code)} onChange={set("g_occ_code")} options={OCCUPATION_OPTIONS} />
                <Field label="Guardian PAN" value={F(s.g_pan)} onChange={set("g_pan")} hint="Leave blank if PAN-exempt" />
                <Field label="PAN-exempt ref no" value={F(s.g_exempt_ref)} onChange={set("g_exempt_ref")} hint="Used only when no PAN" />
                <SelectField label="Guardian relation" value={F(s.g_relation)} onChange={set("g_relation")} options={GUARDIAN_RELATION_OPTIONS} />
              </Group>
            )}

            {variant === "joint" && (
              <Group title="Holders" right={
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs"
                  disabled={holders.length >= 3}
                  onClick={() => setHolders((h) => [...h, emptyHolder()])}><Plus className="h-3 w-3" /> Add</Button>
              }>
                <SelectField label="Holding nature" value={F(s.holding_nature)} onChange={set("holding_nature")} options={HOLDING_OPTIONS} />
                <div className="sm:col-span-2 space-y-3">
                  {holders.map((h, i) => (
                    <div key={i} className="rounded-lg border border-[var(--glass-border)] p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold">Holder {i + 1}{i === 0 ? " (primary)" : ""}</span>
                        {holders.length > 2 && (
                          <button onClick={() => setHolders((hs) => hs.filter((_, j) => j !== i))}
                            className="text-[var(--ink-muted)] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Field label="First name" value={h.first_name}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))} />
                        <Field label="Last name" value={h.last_name}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, last_name: e.target.value } : x))} />
                        <Field label="PAN" value={h.pan}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, pan: e.target.value } : x))} />
                        <Field label="Date of birth" type="date" value={h.dob}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, dob: e.target.value } : x))} />
                        <SelectField label="Gender" value={h.gender} options={GENDER_OPTIONS}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, gender: e.target.value } : x))} />
                        <SelectField label="Occupation" value={h.occ_code} options={OCCUPATION_OPTIONS}
                          onChange={(e) => setHolders((hs) => hs.map((x, j) => j === i ? { ...x, occ_code: e.target.value } : x))} />
                      </div>
                    </div>
                  ))}
                </div>
              </Group>
            )}

            {variant === "non_individual" && (
              <Group title="Ultimate beneficial owners" right={
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs"
                  onClick={() => setUbos((u) => [...u, emptyUbo()])}><Plus className="h-3 w-3" /> Add UBO</Button>
              }>
                <div className="sm:col-span-2 space-y-3">
                  {ubos.map((u, i) => (
                    <div key={i} className="rounded-lg border border-[var(--glass-border)] p-3">
                      <div className="mb-2 flex items-center justify-between">
                        <span className="text-xs font-semibold">UBO {i + 1}</span>
                        {ubos.length > 1 && (
                          <button onClick={() => setUbos((us) => us.filter((_, j) => j !== i))}
                            className="text-[var(--ink-muted)] hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                        )}
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <Field label="First name" value={u.first_name}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))} />
                        <Field label="Last name" value={u.last_name}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, last_name: e.target.value } : x))} />
                        <Field label="PAN" value={u.pan}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, pan: e.target.value } : x))} />
                        <Field label="Date of birth" type="date" value={u.dob}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, dob: e.target.value } : x))} />
                        <Field label="Beneficial ownership %" type="number" min="0" max="100" value={u.beneficiary_percent}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, beneficiary_percent: e.target.value } : x))} />
                        <SelectField label="Controlling-person type" value={u.ubo_type_code} options={UBO_TYPE_OPTIONS}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, ubo_type_code: e.target.value } : x))} />
                        <SelectField label="UBO category" value={u.ubo_category} options={UBO_CATEGORY_OPTIONS}
                          onChange={(e) => setUbos((us) => us.map((x, j) => j === i ? { ...x, ubo_category: e.target.value } : x))} />
                      </div>
                    </div>
                  ))}
                </div>
              </Group>
            )}

            {variant === "non_individual" && (
              <Group title="FATCA classification">
                <SelectField label="Corporate service sector" value={F(s.corporate_service_sector)} onChange={set("corporate_service_sector")} options={CORPORATE_SECTOR_OPTIONS} />
                <SelectField label="FATCA exemption" value={F(s.exemption_code)} onChange={set("exemption_code")} options={EXEMPTION_OPTIONS} />
                <SelectField label="FFI / direct-reporting NFE" value={F(s.ffi_drnfe)} onChange={set("ffi_drnfe")} options={FFI_OPTIONS} />
                <SelectField label="GIIN status" value={F(s.is_giin_avail)} onChange={set("is_giin_avail")} options={GIIN_OPTIONS} />
                <SelectField label="Income slab" value={F(s.income_slab)} onChange={set("income_slab")} options={INCOME_SLAB_OPTIONS} />
              </Group>
            )}

            <Group title="Bank account">
              <Field label="IFSC" value={F(s.bank_ifsc)} onChange={set("bank_ifsc")} placeholder="HDFC0000060" />
              <Field label="Account number" value={F(s.bank_acc)} onChange={set("bank_acc")} />
              <SelectField label="Account type" value={F(s.bank_type)} onChange={set("bank_type")} options={BANK_ACCOUNT_OPTIONS}
                hint={variant === "nri" ? "NRE/NRO selection automatically chooses NE/NO." : undefined} />
            </Group>

            {(variant === "demat" || variant === "both") && (
              <Group title="Depository">
                <SelectField label="Depository" value={F(s.dp_code)} onChange={set("dp_code")} options={DEPOSITORY_OPTIONS} />
                <Field label="DP ID" value={F(s.dp_id)} onChange={set("dp_id")} />
                <Field label="Client ID" value={F(s.dp_client_id)} onChange={set("dp_client_id")} />
              </Group>
            )}

            {variant === "nri" || (variant === "non_individual" && s.f_country) ? (
              <Group title="Foreign address">
                <Field label="Address line" value={F(s.f_line1)} onChange={set("f_line1")} />
                <Field label="City" value={F(s.f_city)} onChange={set("f_city")} />
                <Field label="State / region" value={F(s.f_state)} onChange={set("f_state")} />
                <SelectField label="Country" value={F(s.f_country)} onChange={set("f_country")} options={ISO_COUNTRY_OPTIONS} placeholder="Select ISO-3 country" />
                <Field label="Postal code" value={F(s.f_pincode)} onChange={set("f_pincode")} />
              </Group>
            ) : (
              <Group title="Communication address">
                <Field label="Address line" value={F(s.addr1)} onChange={set("addr1")} />
                <Field label="City" value={F(s.city)} onChange={set("city")} />
                <Field label="State" value={F(s.state)} onChange={set("state")} />
                <Field label="Pincode" value={F(s.pincode)} onChange={set("pincode")} />
                {variant === "non_individual" && (
                  <SelectField label="Foreign country (if non-resident)" value={F(s.f_country)} onChange={set("f_country")}
                    options={ISO_COUNTRY_OPTIONS} placeholder="Resident in India / not applicable"
                    hint="Selecting a country switches the form to the required foreign address for FPI/QFI." />
                )}
              </Group>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Button onClick={() => call(true)} disabled={busy} variant="outline" className="rounded-full" data-testid="ucc-preview-button">
                <Eye className="mr-2 h-4 w-4" /> Preview wire payload
              </Button>
              <Button onClick={() => call(false)} disabled={busy || !capability?.enabled} className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="ucc-submit-button">
                <Send className="mr-2 h-4 w-4" /> {busy ? "Working…" : "Submit to BSE"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* signature: live wire-payload preview */}
        <Card className="lg:sticky lg:top-6 self-start">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Eye className="h-4 w-4 text-[var(--cta)]" /> Wire payload
              <Badge variant="outline" className="ml-auto font-mono text-[10px]">/v2/add_ucc</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {preview ? (
              <pre className="max-h-[70vh] overflow-auto rounded-lg bg-slate-950 p-4 text-[11px] leading-relaxed text-emerald-200"
                data-testid="ucc-preview-json">
                {JSON.stringify(preview, null, 2)}
              </pre>
            ) : (
              <div className="rounded-lg border border-dashed border-[var(--glass-border)] p-8 text-center">
                <p className="text-sm text-[var(--ink-muted)]">
                  Fill the form and choose <span className="font-medium text-[var(--ink)]">Preview wire payload</span> to
                  see the exact JSON that will be sent to BSE — verify it before submitting.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">Bulk individual UCC CSV runner</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[var(--ink-muted)]">Upload up to 100 resident-individual rows and operate BSE&apos;s official postman-only <code>ucc_csv_runner</code> flow. Use the template so every coded field has the correct format.</p>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" className="rounded-full" onClick={downloadBulkTemplate}>Download CSV template</Button>
            <input type="file" accept=".csv,text/csv" onChange={(event) => readBulkCsv(event.target.files?.[0])} className="block rounded-md border p-2 text-sm" />
            {bulkRows.length > 0 && <Badge variant="outline">{bulkRows.length} row(s) ready</Badge>}
          </div>
          {bulkRows.length > 0 && <div className="max-h-44 overflow-auto rounded-lg border"><table className="w-full text-left text-xs"><thead className="sticky top-0 bg-[var(--surface-2)]"><tr><th className="p-2">Client code</th><th className="p-2">Investor</th><th className="p-2">PAN</th><th className="p-2">Account type</th></tr></thead><tbody>{bulkRows.map((row) => <tr key={row.client_code} className="border-t"><td className="p-2 font-mono">{row.client_code}</td><td className="p-2">{row.first_name} {row.last_name}</td><td className="p-2">{row.pan}</td><td className="p-2">{row.account_type}</td></tr>)}</tbody></table></div>}
          {bulkResult && <details className="rounded-lg border bg-slate-950 text-emerald-200" open><summary className="cursor-pointer p-3 text-xs font-semibold">BSE CSV results</summary><pre className="max-h-80 overflow-auto whitespace-pre-wrap border-t border-slate-800 p-3 text-xs">{JSON.stringify(bulkResult, null, 2)}</pre></details>}
          <Button className="rounded-full bg-[var(--cta)]" disabled={bulkBusy || !bulkRows.length || !capability?.enabled} onClick={submitBulkCsv}><Send className="mr-2 h-4 w-4" />{bulkBusy ? "Sending rows to BSE…" : "Run bulk UCC CSV in BSE UAT"}</Button>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">Maintain an existing BSE UCC</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-[var(--ink-muted)]">Every action below calls BSE&apos;s real update_ucc API and is recorded in the operations log.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Client code" value={maint.client_code} onChange={setMaintField("client_code")} placeholder="BSE UCC" />
            <div className="space-y-1.5"><Label className="text-xs text-[var(--ink-muted)]">Update type</Label>
              <select className="h-9 w-full rounded-md border border-input bg-white px-3 text-sm" value={maint.field} onChange={setMaintField("field")}>
                <option value="contact">Holder contact</option><option value="bank">Bank account</option><option value="depository">Demat account</option><option value="nominee">Nomination</option>
                <option value="fatca">FATCA / holder profile</option><option value="comm_addr">Indian communication address</option><option value="foreign_addr">Foreign address</option>
                <option value="holder">Person / full holder details</option><option value="identifier">UCC document / identifier</option><option value="holding_nature">Holding nature / add joint holder</option>
              </select>
            </div>
            {["bank", "depository", "identifier"].includes(maint.field) && <SelectField label="Action" value={maint.mutation} onChange={setMaintField("mutation")} options={[{ value: "add", label: "Add / replace" }, { value: "delete", label: "Delete existing" }]} />}
            {["contact", "nominee", "fatca", "holder"].includes(maint.field) && <SelectField label="Holder rank" value={maint.holder_rank} onChange={setMaintField("holder_rank")} options={[{ value: "1", label: "1 — Primary holder" }, { value: "2", label: "2 — Second holder" }, { value: "3", label: "3 — Third holder" }, { value: "-1", label: "-1 — Guardian" }]} />}
            {maint.field === "contact" && <><Field label="Email" type="email" value={maint.email} onChange={setMaintField("email")} /><Field label="Mobile" value={maint.phone} onChange={setMaintField("phone")} /></>}
            {maint.field === "bank" && <>
              <Field label="IFSC" value={maint.ifsc} onChange={setMaintField("ifsc")} />
              <Field label="Account number" value={maint.acc_num} onChange={setMaintField("acc_num")} />
              <SelectField label="Account type" value={maint.acc_type} onChange={setMaintField("acc_type")} options={BANK_ACCOUNT_OPTIONS} />
              <SelectField label="Account owner" value={maint.account_owner} onChange={setMaintField("account_owner")} options={ACCOUNT_OWNER_OPTIONS} />
            </>}
            {maint.field === "depository" && <>
              <SelectField label="Depository" value={maint.dp_code} onChange={setMaintField("dp_code")} options={DEPOSITORY_OPTIONS} />
              <Field label="DP ID" value={maint.dp_id} onChange={setMaintField("dp_id")} />
              <Field label="Client ID" value={maint.dp_client_id} onChange={setMaintField("dp_client_id")} />
              <Field label="Linked bank account" value={maint.acc_num} onChange={setMaintField("acc_num")} />
              <SelectField label="Account owner" value={maint.account_owner} onChange={setMaintField("account_owner")} options={ACCOUNT_OWNER_OPTIONS} />
            </>}
            {maint.field === "nominee" && <>
              <Field label="Nominee name" value={maint.nominee_name} onChange={setMaintField("nominee_name")} />
              <Field label="Date of birth" type="date" value={maint.nominee_dob} onChange={setMaintField("nominee_dob")} />
              <Field label="PAN (optional)" value={maint.nominee_pan} onChange={setMaintField("nominee_pan")} />
              <SelectField label="Gender" value={maint.nominee_gender} onChange={setMaintField("nominee_gender")} options={GENDER_OPTIONS} />
              <SelectField label="Relation to investor" value={maint.nominee_relation} onChange={setMaintField("nominee_relation")} options={NOMINEE_RELATION_OPTIONS} />
            </>}
            {(maint.field === "fatca" || maint.field === "holder" || maint.field === "holding_nature") && <>
              {maint.field === "holding_nature" && <SelectField label="New holding nature" value={maint.holding_nature} onChange={setMaintField("holding_nature")} options={UCC_HOLDING_OPTIONS} />}
              {(maint.field !== "holding_nature" || maint.holding_nature !== "SI") && <>
                <Field label={maint.field === "holding_nature" ? "Additional holder first name" : "First name"} value={maint.first_name} onChange={setMaintField("first_name")} />
                <Field label="Middle name" value={maint.middle_name} onChange={setMaintField("middle_name")} />
                <Field label="Last name" value={maint.last_name} onChange={setMaintField("last_name")} />
                <Field label="Date of birth" type="date" value={maint.dob} onChange={setMaintField("dob")} />
                <SelectField label="Gender" value={maint.gender} onChange={setMaintField("gender")} options={GENDER_OPTIONS} />
                <Field label="PAN" value={maint.pan} onChange={setMaintField("pan")} />
                <SelectField label="Occupation" value={maint.occ_code} onChange={setMaintField("occ_code")} options={OCCUPATION_OPTIONS} />
              </>}
              {(maint.field === "holder" || (maint.field === "holding_nature" && maint.holding_nature !== "SI")) && <><Field label="Email" type="email" value={maint.email} onChange={setMaintField("email")} /><Field label="Mobile" value={maint.phone} onChange={setMaintField("phone")} /></>}
              {(maint.field === "fatca" || maint.field === "holder") && <>
                <Field label="Place of birth" value={maint.city_of_birth} onChange={setMaintField("city_of_birth")} />
                <SelectField label="Country of birth" value={maint.country_of_birth} onChange={setMaintField("country_of_birth")} options={ISO_COUNTRY_OPTIONS} />
                <SelectField label="Income slab" value={maint.income_slab} onChange={setMaintField("income_slab")} options={INCOME_SLAB_OPTIONS} />
                <SelectField label="Politically exposed" value={maint.pep} onChange={setMaintField("pep")} options={PEP_OPTIONS} />
              </>}
            </>}
            {(maint.field === "comm_addr" || maint.field === "foreign_addr") && <>
              <Field label="Address line 1" value={maint.addr1} onChange={setMaintField("addr1")} />
              <Field label="Address line 2" value={maint.addr2} onChange={setMaintField("addr2")} />
              <Field label="Address line 3" value={maint.addr3} onChange={setMaintField("addr3")} />
              <Field label="City" value={maint.city} onChange={setMaintField("city")} />
              <Field label="State" value={maint.state} onChange={setMaintField("state")} />
              <Field label="Postal code" value={maint.pincode} onChange={setMaintField("pincode")} />
              <SelectField label="Country" value={maint.country} onChange={setMaintField("country")} options={ISO_COUNTRY_OPTIONS} />
            </>}
            {maint.field === "identifier" && <>
              <SelectField label="Document type" value={maint.identifier_type} onChange={setMaintField("identifier_type")} options={IDENTIFIER_OPTIONS} />
              <Field label="Document number" value={maint.identifier_number} onChange={setMaintField("identifier_number")} />
              <div className="space-y-1.5"><Label className="text-xs text-[var(--ink-muted)]">Document file</Label><input type="file" onChange={(event) => maintenanceFile(event.target.files?.[0])} className="block w-full rounded-md border p-2 text-sm" />{maint.file_name && <p className="text-[10px] text-emerald-700">{maint.file_name} · {maint.file_size} bytes</p>}</div>
            </>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full" disabled={maintBusy} onClick={() => updateUcc(true)}><Eye className="mr-2 h-4 w-4" /> Preview update</Button>
            <Button className="rounded-full bg-[var(--cta)]" disabled={maintBusy || !capability?.enabled} onClick={() => updateUcc(false)}><Send className="mr-2 h-4 w-4" /> Submit update to BSE</Button>
            <Button variant="outline" className="rounded-full text-red-700" disabled={maintBusy || !capability?.enabled} onClick={deactivateUcc}>Deactivate UCC</Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">Recent UCC exchange operations</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {ops.length === 0 ? <p className="text-sm text-[var(--ink-muted)]">No UCC operations recorded.</p> : ops.slice(0, 20).map((op, index) => (
            <div key={`${op.at}-${index}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--glass-border)] p-3 text-xs">
              <span><b>{op.client_code}</b> · {op.op}</span>
              <span>{op.result?.status || op.status || "UNKNOWN"} · {op.result?.message || ""} · {op.at?.replace("T", " ").slice(0, 19)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

function HealthPill({ health }) {
  const ok = health.ok;
  return (
    <div className="flex items-center gap-2 rounded-full border border-[var(--glass-border)] px-3 py-1.5"
      data-testid="ucc-health-pill" title={ok ? "All live submissions reconciled" : health.alerts.join(" · ")}>
      <ShieldCheck className={`h-4 w-4 ${ok ? "text-emerald-500" : "text-amber-500"}`} />
      <span className="text-xs font-medium">{ok ? "Money-safety: healthy" : `${health.alerts.length} alert(s)`}</span>
      {health.failed_last_24h > 0 && <Badge className="bg-amber-500/90 text-[10px]">{health.failed_last_24h} failed 24h</Badge>}
    </div>
  );
}
